import { useMsal } from '@azure/msal-react';
import { createContext, Fragment, useContext, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import pageMetaInfo from '../pageMetaInfo';

export const PageInfoContext = createContext();

export const PageInfoProvider = ({ children }) => {
  const [pageInfo, setPageInfo] = useState();
  const { accounts } = useMsal();
  const { title, description } = pageInfo || {};
  const isLoggedIn = accounts.length > 0;

  const getUserStatus = () => {
    if (!isLoggedIn) {
      return null;
    }

    return accounts[0]?.idTokenClaims?.hasOwnProperty('newUser')
      ? 'New User'
      : 'Existing User';
  };

  const removeDuplicatePagesFromDataLayer = () => {
    window.dataLayer.filter((data, index) => {
      return data.hasOwnProperty('page_name')
        ? window.dataLayer.splice(index, 1)
        : data;
    });
  };

  const getDataLayerPayload = (pageInfo) => ({
    // GTM dataLayer
    event: 'DSI Analytics',
    page_name: pageInfo?.title,
    page_type: pageInfo?.pageType,
    page_url: window.location.href,
    loginId: isLoggedIn
      ? accounts[0]?.idTokenClaims.extension_FANCode
      : 'Anonymous',
    loginStatus: isLoggedIn ? 'yes' : 'no',
    logintype: 'FAN',
    userStatus: getUserStatus(),
    dataLayerfanCode: isLoggedIn
      ? accounts[0]?.idTokenClaims?.extension_FANCode
      : null,
    dataLayeruserSub: isLoggedIn
      ? accounts[0]?.idTokenClaims?.sub
      : null,
    dataLayeruserAud: isLoggedIn
      ? accounts[0]?.idTokenClaims?.aud
      : null,
  });

  const pushDataLayer = (pageInfo) => {
    removeDuplicatePagesFromDataLayer();
    window.dataLayer.push(getDataLayerPayload(pageInfo));
  };

  const setPageInfoFromDefault = (pageName, pageTitleTokens) => {
    const info = pageMetaInfo[pageName];

    if (!info) {
      return;
    }

    const pageInfo = {
      ...info,
      ...(typeof info.title === 'function' && {
        title: info.title(pageTitleTokens),
      }),
    };

    setPageInfo(pageInfo);

    if (pageInfo && pageInfo.title) {
      pushDataLayer(pageInfo);
    };
  };

  const navigationAssertive = useMemo(
    () => (title ? `Navigated to ${title}` : ''),
    [title]
  );

  return (
    <Fragment>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      {/* This region is used to announce route changes */}
      <div className="sr-only" aria-live="assertive">
        {navigationAssertive}
      </div>
      <PageInfoContext.Provider value={[pageInfo, setPageInfoFromDefault]}>
        {children}
      </PageInfoContext.Provider>
    </Fragment>
  );
};

export const usePageInfo = () => useContext(PageInfoContext);
